<!-- src/components/stock/StockReportsSummaryCard.vue -->
<template>
  <div class="stock-report-summary">
    <!-- =====================================================
         PRIMARY SUMMARY
         ===================================================== -->
    <div class="summary-grid">
      <div class="summary-item">
        <span class="summary-label"> Opening Stock </span>

        <strong>
          {{ money(report.totals.opening_value) }}
        </strong>
      </div>

      <div class="summary-item">
        <span class="summary-label"> Stock In </span>

        <strong>
          {{ money(report.totals.stock_in_value) }}
        </strong>
      </div>

      <div class="summary-item">
        <span class="summary-label"> Stock Out </span>

        <strong>
          {{ money(report.totals.stock_out_value) }}
        </strong>
      </div>

      <div class="summary-item">
        <span class="summary-label"> Closing Stock </span>

        <strong>
          {{ money(report.totals.closing_value) }}
        </strong>
      </div>

      <div class="summary-item">
        <span class="summary-label"> Theoretical Cost </span>

        <strong>
          {{ money(report.totals.theoretical_cost) }}
        </strong>
      </div>

      <div class="summary-item">
        <span class="summary-label"> Actual Cost </span>

        <strong>
          {{ money(report.totals.actual_cost) }}
        </strong>
      </div>

      <div class="summary-item" :class="varianceClass">
        <span class="summary-label"> Variance </span>

        <strong>
          {{ money(report.totals.variance_value) }}
        </strong>
      </div>

      <div class="summary-item" :class="varianceClass">
        <span class="summary-label"> Variance % </span>

        <strong>
          {{ percent(report.stock_variance_percent) }}
        </strong>
      </div>
    </div>

    <!-- =====================================================
         MOVEMENT BREAKDOWN
         ===================================================== -->
    <div class="movement-section">
      <div class="movement-item">
        <span> Deliveries </span>

        <strong>
          {{ number(report.totals.delivery_units) }}
        </strong>
      </div>

      <div class="movement-item">
        <span> Transfers In </span>

        <strong>
          {{ number(report.totals.transfer_in_units) }}
        </strong>
      </div>

      <div class="movement-item">
        <span> Transfers Out </span>

        <strong>
          {{ number(report.totals.transfer_out_units) }}
        </strong>
      </div>

      <div class="movement-item">
        <span> Products </span>

        <strong>
          {{ report.product_count }}
        </strong>
      </div>
    </div>

    <!-- =====================================================
         MANUAL OVERRIDE AUDIT
         ===================================================== -->
    <div v-if="hasOverrides" class="core-callout core-callout--warning override-warning">
      <i class="material-icons"> info_outline </i>

      <span>
        This count contains manually overridden stock movements:
        {{ overrideText }}.
      </span>
    </div>

    <!-- =====================================================
         GP
         ===================================================== -->
    <div v-if="report.turnover.available" class="gp-grid">
      <div>
        <span> Net Turnover </span>

        <strong>
          {{ money(report.turnover.net) }}
        </strong>
      </div>

      <div>
        <span> Actual GP </span>

        <strong>
          {{ percent(report.gp.actual_percent) }}
        </strong>
      </div>

      <div>
        <span> Theoretical GP </span>

        <strong>
          {{ percent(report.gp.theoretical_percent) }}
        </strong>
      </div>

      <div>
        <span> GP Variance </span>

        <strong>
          {{ percent(report.gp.variance_percent) }}
        </strong>
      </div>
    </div>
  </div>
</template>

<script>
import { buildStockReportData } from "@/scripts/stockPdfReport";

export default {
  name: "StockReportsSummaryCard",

  props: {
    count: {
      type: Object,
      required: true,
    },

    turnover: {
      type: Object,
      default: null,
    },
  },

  computed: {
    report() {
      return buildStockReportData({
        rows: Array.isArray(this.count?.lines) ? this.count.lines : [],

        turnover: this.turnover || this.count?.turnover || null,
      });
    },

    varianceClass() {
      const variance = Number(this.report?.totals?.variance_value) || 0;

      if (variance < 0) {
        return "negative-variance";
      }

      if (variance > 0) {
        return "positive-variance";
      }

      return "";
    },

    hasOverrides() {
      return (
        this.report.totals.stock_in_override_count > 0 ||
        this.report.totals.stock_out_override_count > 0
      );
    },

    overrideText() {
      const parts = [];

      const stockIn = this.report.totals.stock_in_override_count;

      const stockOut = this.report.totals.stock_out_override_count;

      if (stockIn > 0) {
        parts.push(`${stockIn} Stock In`);
      }

      if (stockOut > 0) {
        parts.push(`${stockOut} Stock Out`);
      }

      return parts.join(", ");
    },
  },

  methods: {
    safeNumber(value) {
      const n = Number(value);

      return Number.isFinite(n) ? n : 0;
    },

    money(value) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(this.safeNumber(value));
    },

    number(value) {
      return this.safeNumber(value).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    percent(value) {
      return `${this.safeNumber(value).toFixed(2)}%`;
    },
  },
};
</script>

<style scoped>
.stock-report-summary {
  width: 100%;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border: 1px solid #eceff1;
  border-radius: 6px;
  background: #fafafa;
}

.summary-label {
  color: #78909c;
  font-size: 0.76rem;
}

.summary-item strong {
  color: #263238;
  font-size: 1rem;
}

.positive-variance {
  border-color: #a5d6a7;
  background: #e8f5e9;
}

.positive-variance strong {
  color: #2e7d32;
}

.negative-variance {
  border-color: #ef9a9a;
  background: #ffebee;
}

.negative-variance strong {
  color: #c62828;
}

.movement-section {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  border-radius: 6px;
  background: #eceff1;
}

.movement-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.movement-item span {
  color: #607d8b;
  font-size: 0.75rem;
}

.override-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.8rem;
}

.gp-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.gp-grid > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px;
  border-top: 2px solid #00897b;
  background: #fafafa;
}

.gp-grid span {
  color: #607d8b;
  font-size: 0.75rem;
}

@media only screen and (max-width: 900px) {
  .summary-grid,
  .movement-section,
  .gp-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 600px) {
  .summary-grid,
  .movement-section,
  .gp-grid {
    grid-template-columns: 1fr;
  }
}
</style>
